import MuxPlayer from '@mux/mux-player-react';
import CirclePlayIcon from '@src/svgs/CirclePlayIcon';
import MagnifyingGlassIcon from '@src/svgs/MagnifyingGlassIcon';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import {
  TRANSCRIPT_PAUSE_EVENT,
  TRANSCRIPT_SEEK_EVENT,
} from '@src/utility/transcriptEvents';
import { reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { MagnifierRegionIndicator, useMagnifierMouse } from './VideoMagnifier';

import type { PlayerVolumeControls } from '@src/hooks/usePlayerVolume';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptSeekDetail } from '@src/utility/transcriptEvents';
import type { CSSProperties } from 'react';

/**
 * Story-absolute window of the source video this player is bounded to
 * (the clip-scoped Translation Review entry, PLAT-10286). When set,
 * `videoCreator.time` is RANGE-LOCAL (0 = `start`): the player converts at
 * its own boundary (`player.currentTime = start + videoCreator.time`),
 * clamps every seek into the window, and pauses at `end` — in place,
 * deliberately unlike `MuxPlayerClip`, which loops back to the clip start.
 * Absent = today's behavior: offset 0, bounded by the video's duration.
 */
export interface VideoTimeRange {
  start: number;
  end: number;
}

const logger = LogManager.getLogger('TranscriptVideoPlayer');

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="var(--accent)">
    <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
    <path d="M9 5.5L5 1.5L1 5.5" stroke="var(--accent)" strokeWidth="2" />
  </svg>
);

// Transport control icons (compact 12x12 inline SVGs)
const SkipToStartIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <rect x="1" y="2" width="2" height="8" rx="0.5" />
    <path d="M11 2.5v7a.5.5 0 0 1-.77.42l-5.5-3.5a.5.5 0 0 1 0-.84l5.5-3.5A.5.5 0 0 1 11 2.5z" />
  </svg>
);

const JogBackIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <path
      d="M7 1.5v9a.5.5 0 0 1-.77.42l-5-3.5a.5.5 0 0 1 0-.84l5-3.5A.5.5 0 0 1 7 3.5z"
      transform="translate(4 0)"
    />
    <path
      d="M7 1.5v9a.5.5 0 0 1-.77.42l-5-3.5a.5.5 0 0 1 0-.84l5-3.5A.5.5 0 0 1 7 3.5z"
      transform="translate(-1 0)"
    />
  </svg>
);

const SmallPlayIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <path d="M3 2.5v7a.5.5 0 0 0 .77.42l5.5-3.5a.5.5 0 0 0 0-.84l-5.5-3.5A.5.5 0 0 0 3 2.5z" />
  </svg>
);

const SmallPauseIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <rect x="2.5" y="2" width="2.5" height="8" rx="0.5" />
    <rect x="7" y="2" width="2.5" height="8" rx="0.5" />
  </svg>
);

const JogForwardIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <path
      d="M2 2.5v7a.5.5 0 0 0 .77.42l5-3.5a.5.5 0 0 0 0-.84l-5-3.5A.5.5 0 0 0 2 2.5z"
      transform="translate(-1 0)"
    />
    <path
      d="M2 2.5v7a.5.5 0 0 0 .77.42l5-3.5a.5.5 0 0 0 0-.84l-5-3.5A.5.5 0 0 0 2 2.5z"
      transform="translate(4 0)"
    />
  </svg>
);

const SkipToEndIcon = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="currentColor">
    <path d="M1 2.5v7a.5.5 0 0 0 .77.42l5.5-3.5a.5.5 0 0 0 0-.84l-5.5-3.5A.5.5 0 0 0 1 2.5z" />
    <rect x="9" y="2" width="2" height="8" rx="0.5" />
  </svg>
);

const playerStyle = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  border: 'none',
  '--pip-button': 'none',
  '--center-controls': 'none',
  '--controls': 'none',
} as CSSProperties;

/**
 * Standalone video player for the Transcript & Speakers tab.
 * Syncs bidirectionally with videoCreator.time so the Transcript
 * component's cursor and auto-scroll follow playback.
 */
/** Minimum visible window in seconds when fully zoomed in (matches waveform) */
const MIN_VISIBLE_SECONDS = 10;

/**
 * PLAT-11657 — what a host's own transport bar needs from a player: the verbs,
 * whether it is ready to take them, and how long the video is. Both review
 * players (this one on the story entry, the clip renderer on the clip entry)
 * report this shape, so one bar drives either.
 */
export interface ExternalPlayerControls {
  play: () => void;
  pause: () => void;
  /** Seek, in the player's own clock (range-relative for a bounded player). */
  setTime: (time: number) => void;
  isReady: boolean;
  duration: number;
  /** Master volume + mute, for the bar's volume control. */
  volume?: PlayerVolumeControls;
}

interface TranscriptVideoPlayerProps {
  zoomLevel: number;
  videoCreator: ITranscriptVideoCreator;
  /**
   * PLAT-11228 (audio-first): object URL of the session's locally-held
   * upload — the mp4-chain fallback while renditions are pending. Passed
   * down (not read from a store) so this leaf stays presentation-only.
   */
  localPreviewUrl?: string | null;
  onMagnifierUpdate?: (pos: { x: number; y: number } | null) => void;
  onVideoElementReady?: (el: HTMLVideoElement | null) => void;
  /** See {@link VideoTimeRange}. Optional, default-off (PLAT-10286). */
  timeRange?: VideoTimeRange;
  /**
   * Highlight-only playback (PLAT-10949): story-absolute time windows, sorted
   * by start with no overlaps (`highlightRangesToPlaybackRanges` guarantees
   * both). While playing, crossing a window's end jumps straight to the next
   * window's start — the gaps between highlights are skipped — and the end of
   * the LAST window pauses in place; pressing play there rewinds to the first
   * window. Scrubbing/seeking is NOT clamped: a paused player can park
   * anywhere, and playback simply skips ahead from a gap on the next frame.
   * Ignored when `timeRange` is set (the two bounding modes don't compose).
   */
  playbackSegments?: VideoTimeRange[];
  /**
   * Seek the player to `videoCreator.time` once metadata loads, so it opens
   * parked at the shared playhead (the review "continue from the paused
   * frame" behavior). The bounded clip entry already does this via
   * `timeRange`; this flag extends it to the unbounded story entry. The
   * page player leaves it unset and stays at 0 on load. (PLAT-10286)
   */
  anchorToCreatorTimeOnLoad?: boolean;
  /**
   * LOADING PLACEHOLDER for a review player. The review player opens on the
   * whole-story video and has to load + seek to the paused position before
   * the real frame decodes (seconds for a clip deep in the story) — leaving a
   * black gap. This poster (the clip's rendered thumbnail) fills that gap and
   * is CLEARED once the seeked frame is ready, mirroring the clip preview's
   * thumbnail→frame transition. Absent ⇒ the story cover is the placeholder.
   * (PLAT-10286)
   */
  poster?: string;
  /**
   * Mute the video's own audio track. Set in Dubbed mode (PLAT-10597) so the
   * video plays as the muted clock/visual while the composed dub is heard.
   */
  muted?: boolean;
  /**
   * PLAT-11927 — the video's own audio as a share of the bar's volume, 0–1
   * (default 1). Dubbing Review's Original level under the dub on Both.
   */
  audioLevel?: number;
  /**
   * PLAT-11657 — a host that renders its own transport bar (the Translation
   * Review) takes over the controls: this player's header row (the jog buttons
   * and the Hide Video toggle) and its centre play/pause overlay both stand
   * down, so there is one set of controls on screen — the clip preview's rule.
   */
  externalControls?: boolean;
  /**
   * PLAT-11657 — report the verbs and readiness a host's transport bar drives;
   * `null` on unmount. Re-reported whenever readiness or duration changes.
   */
  onControls?: (controls: ExternalPlayerControls | null) => void;
  /**
   * PLAT-11683 — no magnifier toggle over the picture. The Translation Review
   * player carries captions and a Captions picker instead; the magnifier is
   * the Transcript & Speakers tab's own tool.
   */
  hideMagnifier?: boolean;
}

const TranscriptVideoPlayer: React.FC<TranscriptVideoPlayerProps> = observer(
  ({
    zoomLevel,
    hideMagnifier = false,
    onMagnifierUpdate,
    onVideoElementReady,
    videoCreator,
    localPreviewUrl,
    anchorToCreatorTimeOnLoad,
    timeRange,
    playbackSegments,
    poster,
    muted,
    audioLevel = 1,
    externalControls = false,
    onControls,
  }) => {
    const muxPlayerRef = useRef<any>(null);
    // PLAT-11657 — the bar's volume control, over the Mux element. The host's
    // `muted` (Dubbed mode silences the picture) and the user's mute compose:
    // either silences; unmuting by the user does not un-silence a dub.
    const [volume, setVolumeState] = useState(1);
    const [userMuted, setUserMuted] = useState(false);
    useEffect(() => {
      const player = muxPlayerRef.current;
      if (!player) return;
      player.volume = volume * Math.max(0, Math.min(1, audioLevel));
      player.muted = (muted ?? false) || userMuted;
    }, [volume, audioLevel, userMuted, muted]);
    const setVolume = useCallback((next: number) => {
      const clamped = Math.max(0, Math.min(1, next));
      setVolumeState(clamped);
      if (clamped > 0) setUserMuted(false);
    }, []);
    const toggleMute = useCallback(() => setUserMuted(value => !value), []);
    const volumeControls = useMemo<PlayerVolumeControls>(
      () => ({
        volume,
        muted: userMuted || volume === 0,
        setVolume,
        toggleMute,
      }),
      [volume, userMuted, setVolume, toggleMute],
    );
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    // Review player only: once the element has decoded the frame at the
    // paused position, drop the loading placeholder so the real frame shows
    // (PLAT-10286).
    const [frameReady, setFrameReady] = useState(false);
    const [isMagnifierActive, setIsMagnifierActive] = useState(false);
    const [isVideoVisible, setIsVideoVisible] = useState(true);
    // PLAT-11228: set when the local blob src errored — drops it from the
    // source chain for this render so the player falls back to renditions.
    const [localPreviewFailed, setLocalPreviewFailed] = useState(false);
    useEffect(() => setLocalPreviewFailed(false), [localPreviewUrl]);
    const usableLocalPreviewUrl = localPreviewFailed ? null : localPreviewUrl;

    const getVideoElement = useCallback((): HTMLVideoElement | null => {
      const player = muxPlayerRef.current;
      if (!player) return null;
      return player.media?.nativeEl ?? player.querySelector?.('video') ?? null;
    }, []);

    const magnifierMousePos = useMagnifierMouse(
      isMagnifierActive,
      videoContainerRef.current,
    );

    // Forward magnifier state to parent for rendering the preview panel
    useEffect(() => {
      onMagnifierUpdate?.(magnifierMousePos);
    }, [magnifierMousePos, onMagnifierUpdate]);

    // Notify parent when video element becomes available
    useEffect(() => {
      if (isLoaded) {
        onVideoElementReady?.(getVideoElement());
      }
      return () => onVideoElementReady?.(null);
    }, [isLoaded, getVideoElement, onVideoElementReady]);

    // Imperatively mirror the `muted` prop onto the underlying media element.
    // The MuxPlayer `muted` prop alone can fail to silence a stream that is
    // already playing (media-element gesture/autoplay caching), so in Dubbed
    // mode the original audio kept playing UNDER the composed dub — two tracks
    // at once (PLAT-10600). Forcing `el.muted` on the native element guarantees
    // Dubbed mode plays only the dub, and Both/Original keep the original.
    useEffect(() => {
      const el = getVideoElement();
      if (el) el.muted = muted ?? false;
    }, [muted, isLoaded, getVideoElement]);

    // Feedback loop prevention: timestamp of last RAF update we sent
    const preventFeedbackRef = useRef<number>(0);
    // Suppress RAF sync during programmatic seeks
    const isSeekingRef = useRef(false);
    // Track last synced time to avoid redundant updates
    const lastSyncedTimeRef = useRef<number>(0);

    // Always use the original video (not final/feature video).
    // Prefer direct MP4 URL for fast load (matches the top-level Content Studio
    // player behavior). Only fall back to Mux streaming when no MP4 is available.
    const story = videoCreator.story;
    const originalVideo = story?.originalVideo;
    // PLAT-11224/11228: while renditions are pending, never hand the player
    // the unprocessed original (`mp4Url` is the raw upload until a high
    // rendition exists) — take whatever compressed rendition already landed,
    // else the session's locally-held upload when passed.
    const directMp4Url = isCompressedRenditionPending(story)
      ? originalVideo?.video?.mp4UrlMedium ||
        originalVideo?.video?.mp4UrlLow ||
        usableLocalPreviewUrl ||
        undefined
      : originalVideo?.video?.mp4UrlHigh ||
        originalVideo?.video?.mp4UrlHd ||
        originalVideo?.video?.mp4Url ||
        originalVideo?.video?.mp4UrlMedium ||
        originalVideo?.video?.mp4UrlLow ||
        usableLocalPreviewUrl ||
        undefined;
    const originalPlaybackId = directMp4Url
      ? undefined
      : originalVideo?.video?.muxPlaybackId;
    // A review player opens parked on the seeked video frame, but the
    // whole-story video takes a moment to load + seek + decode. Show the
    // clip's rendered thumbnail (`poster`, falling back to the story cover) as
    // a LOADING PLACEHOLDER until that frame is ready, then drop it so the real
    // frame shows — mirroring the clip preview's thumbnail→frame transition. A
    // permanent poster masks the seeked frame (mux-player keeps the poster
    // painted until the video plays), so it must clear on `frameReady`.
    //
    // The page player has the same problem: seeking via the transcript/speakers
    // moves the element's currentTime, but a permanent cover poster keeps
    // hiding the seeked frame so the thumbnail never matches the playhead. So
    // both kinds use the cover only as the pre-seek placeholder and clear it on
    // `frameReady` (set by the first 'seeked'), revealing the real frame while
    // still avoiding a black gap on initial load. (PLAT-10286)
    const isReviewPlayer = Boolean(timeRange || anchorToCreatorTimeOnLoad);
    // PLAT-11228: no story thumbnail exists yet while renditions are pending —
    // a poster URL would paint as a broken image over the local preview.
    const storyThumbnailUrl = isCompressedRenditionPending(story)
      ? undefined
      : originalVideo?.video?.thumbnailUrl;
    const posterUrl = frameReady
      ? undefined
      : isReviewPlayer
        ? (poster ?? storyThumbnailUrl)
        : storyThumbnailUrl;
    const videoUrl = directMp4Url;

    // A blob src can die under the player (revoked object URL, undecodable
    // file). Log it and, when the local source was the one playing, drop it
    // so the chain falls through to renditions (PLAT-11228).
    const handleError = useCallback(
      (event: Event) => {
        const mediaError = getVideoElement()?.error;
        logger.warn('Video element error', {
          src: videoUrl,
          eventType: event.type,
          ...extractErrorInfo(mediaError ?? event),
        });
        if (videoUrl?.startsWith('blob:')) {
          setLocalPreviewFailed(true);
        }
      },
      [getVideoElement, videoUrl],
    );

    // Range-local <-> player-space conversion (PLAT-10286). With no timeRange
    // the offset is 0 and the clamp ceiling is the video's duration — exactly
    // the pre-existing behavior. videoCreator.time is ALWAYS range-local.
    const rangeStart = timeRange?.start ?? 0;
    const rangeLength = timeRange ? timeRange.end - timeRange.start : undefined;

    // Single clamp path: every seek — transport buttons, the seek event, the
    // MobX reaction — funnels through this. Input/output are range-local.
    const clampTime = useCallback(
      (newTime: number): number => {
        // Range length is a known constant, so a bounded player clamps
        // correctly even before loadedmetadata (duration would read 0).
        const max = rangeLength ?? muxPlayerRef.current?.duration ?? 0;
        return Math.max(0, Math.min(newTime, max || 0));
      },
      [rangeLength],
    );

    const handleLoadedMetadata = useCallback(() => {
      setIsLoaded(true);
      // Open parked at the shared playhead. A bounded (clip) player must sit
      // inside its window from the first frame; the unbounded (story) review
      // player must open at the paused position too ("continue from the
      // paused frame"). Either way, seek to videoCreator.time now — seeks
      // issued before metadata loaded may have been dropped by the browser.
      // The page player passes neither flag and stays at 0 on load. (PLAT-10286)
      if (timeRange || anchorToCreatorTimeOnLoad) {
        const player = muxPlayerRef.current;
        if (player) {
          const clamped = clampTime(videoCreator.time);
          const target = rangeStart + clamped;
          if (Math.abs((player.currentTime ?? 0) - target) > 0.1) {
            // Real seek needed — the 'seeked' handler clears the placeholder
            // once the target frame has decoded.
            player.currentTime = target;
            lastSyncedTimeRef.current = clamped;
            preventFeedbackRef.current = Date.now();
          } else {
            // Already at the target (e.g. story entry opening at 0) — the
            // current frame is the right one, so no seek fires; reveal it.
            setFrameReady(true);
          }
        }
      }
    }, [
      timeRange,
      anchorToCreatorTimeOnLoad,
      clampTime,
      rangeStart,
      videoCreator,
    ]);

    // Clear the review player's loading placeholder once a seek completes —
    // the seek to the paused position (above) finishing is the precise "frame
    // retrieved" signal. Idempotent: later transport/click seeks re-fire it
    // harmlessly. (PLAT-10286)
    const handleSeeked = useCallback(() => {
      setFrameReady(true);
    }, []);

    // RAF loop for Video -> Transcript time sync (same pattern as TranscriptView)
    useEffect(() => {
      let rafId: number;
      const sync = () => {
        if (!isSeekingRef.current) {
          const player = muxPlayerRef.current;
          // End-of-range: pause in place + clear the shared playing flag
          // (same shape as the 'transcript-pause' handler below).
          if (
            timeRange &&
            player &&
            videoCreator.isPlaying &&
            player.currentTime >= timeRange.end
          ) {
            player.pause();
            // Snap the element back onto the boundary — otherwise the next
            // sync frame reads the overshot currentTime and drags the shared
            // clock past the clip's end.
            player.currentTime = timeRange.end;
            const endLocal = timeRange.end - timeRange.start;
            lastSyncedTimeRef.current = endLocal;
            preventFeedbackRef.current = Date.now();
            runInAction(() => {
              videoCreator.isPlaying = false;
              videoCreator.time = endLocal;
            });
            rafId = requestAnimationFrame(sync);
            return;
          }
          // Highlight-only playback (PLAT-10949): with playbackSegments set
          // (and no timeRange — rangeStart is 0, so player time IS
          // videoCreator time), skip the gaps while playing. Crossing a
          // window's end (or scrubbing into a gap mid-playback) jumps to the
          // next window's start; the end of the last window pauses in place
          // (same shape as the timeRange guard above).
          if (
            !timeRange &&
            playbackSegments &&
            playbackSegments.length > 0 &&
            player &&
            videoCreator.isPlaying
          ) {
            const t = player.currentTime;
            const lastSeg = playbackSegments[playbackSegments.length - 1];
            if (t >= lastSeg.end) {
              player.pause();
              player.currentTime = lastSeg.end;
              lastSyncedTimeRef.current = lastSeg.end;
              preventFeedbackRef.current = Date.now();
              runInAction(() => {
                videoCreator.isPlaying = false;
                videoCreator.time = lastSeg.end;
              });
              rafId = requestAnimationFrame(sync);
              return;
            }
            // First window that hasn't ended yet; if the playhead sits before
            // it (in a gap, or before the first highlight), jump ahead. The
            // 50ms tolerance avoids re-seeking on frames where currentTime
            // has just landed on a window start.
            const next = playbackSegments.find(s => t < s.end);
            if (next && t < next.start - 0.05) {
              player.currentTime = next.start;
              lastSyncedTimeRef.current = next.start;
              preventFeedbackRef.current = Date.now();
              runInAction(() => {
                videoCreator.time = next.start;
              });
              rafId = requestAnimationFrame(sync);
              return;
            }
          }
          let videoTime = (player?.currentTime ?? 0) - rangeStart;
          if (timeRange) {
            // Defensive: no sync write may land outside the bound.
            videoTime = Math.max(
              0,
              Math.min(videoTime, timeRange.end - timeRange.start),
            );
          }
          if (
            !Number.isNaN(videoTime) &&
            Math.abs(videoTime - lastSyncedTimeRef.current) > 0.15
          ) {
            lastSyncedTimeRef.current = videoTime;
            preventFeedbackRef.current = Date.now();
            runInAction(() => {
              videoCreator.time = videoTime;
            });
          }
        }
        rafId = requestAnimationFrame(sync);
      };
      rafId = requestAnimationFrame(sync);
      return () => cancelAnimationFrame(rafId);
    }, [videoCreator, timeRange, playbackSegments, rangeStart]);

    // Transcript -> Video: seek player when videoCreator.time changes externally
    useEffect(() => {
      const dispose = reaction(
        () => videoCreator.time,
        newTime => {
          // Skip if this change came from our own RAF sync
          if (Date.now() - preventFeedbackRef.current < 100) return;
          const player = muxPlayerRef.current;
          if (
            player &&
            Math.abs(player.currentTime - (rangeStart + newTime)) > 0.3
          ) {
            isSeekingRef.current = true;
            // Bounded: share seekTo's clamp so the range lives in one place.
            // UNbounded: keep the pre-existing raw write byte-for-byte —
            // clamping here against a pre-metadata duration (0) would snap
            // external seeks to 0, a default-on behavior change (D9).
            const next = timeRange ? clampTime(newTime) : newTime;
            player.currentTime = rangeStart + next;
            lastSyncedTimeRef.current = next;
            setTimeout(() => {
              isSeekingRef.current = false;
            }, 50);
          }
        },
      );
      return dispose;
    }, [videoCreator, rangeStart, clampTime, timeRange]);

    // Reset isPlaying on unmount to avoid stale state
    useEffect(() => {
      return () => {
        runInAction(() => {
          videoCreator.isPlaying = false;
        });
      };
    }, [videoCreator]);

    // Adaptive shuttle: ~20-25 jumps per visible window, snapped to round values.
    // Depends on isLoaded so it recomputes once player.duration is available.
    // A bounded player jogs relative to its window, not the whole video.
    const shuttleStep = useMemo(() => {
      const playerDuration =
        rangeLength ?? (muxPlayerRef.current?.duration || 0);
      if (playerDuration <= 0) return 1;
      const windowDuration =
        playerDuration -
        zoomLevel *
          (playerDuration - Math.min(MIN_VISIBLE_SECONDS, playerDuration));
      const idealStep = windowDuration / 22;
      // Snap to the closest round step
      const steps = [1, 5, 10, 15, 20, 30, 60, 120, 300];
      let best = steps[0];
      for (const s of steps) {
        if (Math.abs(s - idealStep) <= Math.abs(best - idealStep)) best = s;
      }
      return best;
    }, [zoomLevel, isLoaded]);

    const seekTo = useCallback(
      (newTime: number) => {
        const player = muxPlayerRef.current;
        if (!player) return;
        const clamped = clampTime(newTime);
        player.currentTime = rangeStart + clamped;
        preventFeedbackRef.current = Date.now();
        lastSyncedTimeRef.current = clamped;
        runInAction(() => {
          videoCreator.time = clamped;
        });
      },
      [videoCreator, clampTime, rangeStart],
    );

    // Listen for external seek requests (e.g., speaker panel click, transcript speaker label click).
    // During playback the RAF-based feedback prevention blocks the MobX reaction,
    // so external callers dispatch a 'transcript-seek' custom event instead.
    // Scoped payloads ({ time, creator }) are honored only when the creator is
    // OUR videoCreator — object identity is the review-sandbox boundary
    // (PLAT-10286). Legacy bare-number details stay global.
    useEffect(() => {
      const handleSeekEvent = (e: Event) => {
        const detail = (e as CustomEvent<TranscriptSeekDetail | number>).detail;
        if (typeof detail === 'number') {
          seekTo(detail);
          return;
        }
        if (detail && typeof detail.time === 'number') {
          if (detail.creator && detail.creator !== videoCreator) return;
          seekTo(detail.time);
        }
      };
      window.addEventListener(TRANSCRIPT_SEEK_EVENT, handleSeekEvent);
      return () =>
        window.removeEventListener(TRANSCRIPT_SEEK_EVENT, handleSeekEvent);
    }, [seekTo, videoCreator]);

    // Listen for external pause requests (the Translation Review workspace
    // opening mid-playback). Symmetric with 'transcript-seek': inert unless
    // dispatched. Pauses THIS player and clears the shared isPlaying so no
    // audio stream is left running behind a paused-looking UI (PLAT-10263
    // double-audio edge). Every mounted player handles it, so the background
    // page player stops too.
    useEffect(() => {
      const handlePauseEvent = () => {
        muxPlayerRef.current?.pause();
        runInAction(() => {
          videoCreator.isPlaying = false;
        });
      };
      window.addEventListener(TRANSCRIPT_PAUSE_EVENT, handlePauseEvent);
      return () =>
        window.removeEventListener(TRANSCRIPT_PAUSE_EVENT, handlePauseEvent);
    }, [videoCreator]);

    const pause = useCallback(() => {
      const player = muxPlayerRef.current;
      if (!player) return;
      player.pause();
      runInAction(() => {
        videoCreator.isPlaying = false;
      });
    }, [videoCreator]);

    const play = useCallback(() => {
      const player = muxPlayerRef.current;
      if (!player) return;
      // Bounded player parked at the range end: play means "watch the
      // clip again" — rewind to the range start first, otherwise the
      // end-of-range guard re-pauses on the next frame (dead button).
      if (timeRange && player.currentTime >= timeRange.end) {
        seekTo(0);
      } else if (!timeRange && playbackSegments && playbackSegments.length) {
        // Highlight-only playback parked at the last highlight's end: play
        // means "watch the highlights again" — rewind to the first one
        // (PLAT-10949). A playhead in a gap needs nothing here; the RAF
        // skip-ahead moves it on the first playing frame.
        const lastSeg = playbackSegments[playbackSegments.length - 1];
        if (player.currentTime >= lastSeg.end) {
          seekTo(playbackSegments[0].start);
        }
      }
      const playPromise = player.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            runInAction(() => {
              videoCreator.isPlaying = true;
            });
          })
          .catch((error: any) => {
            logger.error('Error playing video:', error);
            runInAction(() => {
              videoCreator.isPlaying = false;
            });
          });
      }
    }, [videoCreator, timeRange, playbackSegments, seekTo]);

    // One toggle for the on-screen control and the header's transport button,
    // so the two can never drift apart.
    const handlePlayPause = useCallback(() => {
      if (videoCreator.isPlaying) pause();
      else play();
    }, [videoCreator, play, pause]);

    // PLAT-11657 — hand a host's transport bar the verbs. `duration` is the
    // player's own clock: the window for a bounded player, the video for an
    // unbounded one; both read `null` until metadata lands, hence `isLoaded`
    // in the deps so the bar learns the length as soon as it exists.
    useEffect(() => {
      if (!onControls) return;
      onControls({
        play,
        pause,
        setTime: seekTo,
        isReady: isLoaded,
        duration: rangeLength ?? (muxPlayerRef.current?.duration || 0),
        volume: volumeControls,
      });
      return () => onControls(null);
    }, [
      onControls,
      play,
      pause,
      seekTo,
      isLoaded,
      rangeLength,
      volumeControls,
    ]);

    if (!originalPlaybackId && !videoUrl) {
      return <NoVideoMessage>No video available for preview</NoVideoMessage>;
    }

    return (
      <VideoContainer>
        {!externalControls && (
          <VideoHeaderRow>
            <TransportControls>
              <TransportButton onClick={() => seekTo(0)} title="Skip to start">
                <SkipToStartIcon />
              </TransportButton>
              <TransportButton
                onClick={() => seekTo(videoCreator.time - shuttleStep)}
                title={`Jog reverse (${shuttleStep}s)`}
              >
                <JogBackIcon />
              </TransportButton>
              <TransportButton
                onClick={handlePlayPause}
                title={videoCreator.isPlaying ? 'Pause' : 'Play'}
              >
                {videoCreator.isPlaying ? (
                  <SmallPauseIcon />
                ) : (
                  <SmallPlayIcon />
                )}
              </TransportButton>
              <TransportButton
                onClick={() => seekTo(videoCreator.time + shuttleStep)}
                title={`Jog forward (${shuttleStep}s)`}
              >
                <JogForwardIcon />
              </TransportButton>
              <TransportButton
                onClick={() => seekTo(Infinity)}
                title="Skip to end"
              >
                <SkipToEndIcon />
              </TransportButton>
            </TransportControls>
            <VisibilityToggle onClick={() => setIsVideoVisible(prev => !prev)}>
              <VisibilityLabel>
                {isVideoVisible ? 'Hide Video' : 'Show Video'}
              </VisibilityLabel>
              <ToggleArrow $collapsed={!isVideoVisible}>
                <ChevronDownIcon />
              </ToggleArrow>
            </VisibilityToggle>
          </VideoHeaderRow>
        )}
        <VideoPreviewContainer
          ref={videoContainerRef}
          $collapsed={!isVideoVisible}
        >
          <MuxPlayer
            ref={muxPlayerRef}
            style={playerStyle}
            streamType="on-demand"
            playbackId={originalPlaybackId}
            src={videoUrl}
            poster={posterUrl}
            preload="auto"
            muted={(muted ?? false) || userMuted}
            crossOrigin="anonymous"
            onLoadedMetadata={handleLoadedMetadata}
            onSeeked={handleSeeked}
            onError={handleError}
          />
          {!isLoaded && <LoadingOverlay>Loading video...</LoadingOverlay>}
          {isLoaded && !externalControls && (
            <PlayPauseOverlay
              $alwaysVisible={!videoCreator.isPlaying}
              onClick={handlePlayPause}
            >
              {videoCreator.isPlaying ? <PauseIcon /> : <CirclePlayIcon />}
            </PlayPauseOverlay>
          )}
          {!hideMagnifier && (
            <>
              <MagnifierRegionIndicator mousePos={magnifierMousePos} />
              <MagnifierToggle
                $isActive={isMagnifierActive}
                onClick={() => setIsMagnifierActive(prev => !prev)}
                title={
                  isMagnifierActive ? 'Disable magnifier' : 'Enable magnifier'
                }
              >
                <MagnifyingGlassIcon size={14} />
              </MagnifierToggle>
            </>
          )}
        </VideoPreviewContainer>
      </VideoContainer>
    );
  },
);

const VideoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #000;
`;

const VideoHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--background);
`;

const TransportControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const VisibilityLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const VisibilityToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--radius-input);
  transition: all var(--transition-normal);

  &:hover {
    background: var(--border);

    ${VisibilityLabel} {
      color: var(--text-secondary);
    }
  }
`;

const ToggleArrow = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  transform: ${props => (props.$collapsed ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

const TransportButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: var(--radius-input);
  color: var(--foreground);
  cursor: pointer;
  padding: 0;
  transition:
    color var(--transition-normal),
    background var(--transition-normal);

  &:hover {
    background: var(--border);
  }
`;

const VideoPreviewContainer = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background-color: #000000;
  max-height: ${props => (props.$collapsed ? '0' : '1000px')};
  transition: max-height 0.25s ease;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: var(--text-muted);
  font-size: var(--text-sm);
  z-index: 1;
`;

const PlayPauseOverlay = styled.div<{ $alwaysVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: ${({ $alwaysVisible }) => ($alwaysVisible ? 1 : 0)};
  transition: opacity var(--transition-normal);
  cursor: pointer;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
`;

const MagnifierToggle = styled.button<{ $isActive: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-input);
  cursor: pointer;
  padding: 0;
  background: ${({ $isActive }) =>
    $isActive ? 'var(--accent)' : 'rgba(0, 0, 0, 0.5)'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--background)' : 'rgba(255, 255, 255, 0.8)'};
  transition:
    background var(--transition-normal),
    color var(--transition-normal);

  &:hover {
    background: ${({ $isActive }) =>
      $isActive ? 'var(--accent)' : 'rgba(0, 0, 0, 0.7)'};
    color: #fff;
  }
`;

const NoVideoMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-muted);
  font-style: italic;
  padding: 16px;
  background: #000;
`;

export default TranscriptVideoPlayer;
